import { useMutation } from "@apollo/client/react";
import { Typography } from "@mui/material";
import MuiButton from "@mui/material/Button";
import { styled, useTheme } from "@mui/material/styles";
import {
  Action,
  Dialog,
  DialogActions,
  DialogContent,
  RenderHTML,
  Tooltip,
  UserSearch,
  UserSelected,
} from "components";
import { useFocusStyles } from "hooks/focusBorder";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "store";
import portalSettingsManager from "utils/portalSettingsManager";
import { SHARE_CONTENT } from "utils/queries";
import { find } from "lodash";
import { useViewport } from "hooks/useViewport";

const Button = styled(MuiButton)(({ theme }) => ({
  borderRadius: 0,
  color: portalSettingsManager.buttonColors?.themed?.active?.text || theme.palette.primary.main,
  height: "42px",
  width: "120px",
  fontSize: 11,
  fontWeight: 700,
  "&.MuiButton-outlined": {
    border: `1px solid ${
      portalSettingsManager.buttonColors?.themed?.active?.border || theme.palette.primary.main
    }`,
  },
  "&.Mui-disabled": {
    color: theme.palette.grey["500"],
  },
}));

type ShareDialogProps = {
  open: boolean;
  handleClose: () => void;
  shareLink?: string;
  [x: string]: any;
};
export const ShareDialog: React.FC<ShareDialogProps> = ({
  open,
  handleClose,
  course,
  contentType,
  shareLink,
}) => {
  const theme = useTheme();
  const { isMobile } = useViewport();
  const dispatch = useAppDispatch();
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const [addedUsers, setaddedUsers] = useState<any[]>([]);
  const [shareContent] = useMutation(SHARE_CONTENT);
  const [shareTitle, setShareTitle] = useState<string>("");
  const [statusMessage, setStatusMessage] = useState<string>("");
  const { t } = useTranslation();
  const handleRemoveEnrollment = (user: any) => {
    let removedUser: any = [];
    removedUser = find(selectedUsers, ["id", user.id]);
    setSelectedUsers(selectedUsers.filter((item: any) => item.id !== user.id));
    if (removedUser) {
      setStatusMessage(removedUser.fullName + " is removed");
    }
  };
  useEffect(() => {
    setShareTitle("Share " + course?.name);
  }, [course]);
  const handleShareContent = () => {
    handleClose();
    dispatch({
      type: "loader/showandhide",
      payload: { show: true, message: t("share_content_loading") },
    });
    const users = selectedUsers.map((i: any) => {
      const obj = {
        email: i.email,
      };
      return obj;
    });
    const payloadData = {
      recipients: users,
      id: course?.id,
      contentType: contentType,
    };
    shareContent({ variables: { payload: payloadData } })
      .then(() => {
        dispatch({
          type: "loader/showandhide",
          payload: { show: false, message: t("share_content_loading") },
        });
        setSelectedUsers([]);
        dispatch({
          type: "alert/show",
          payload: {
            type: "success",
            title: t("sharedcontent_success_alert_title"),
            message: t("sharedcontent_success_alert_message"),
          },
        });
      })
      .catch((e) => {
        dispatch({
          type: "loader/showandhide",
          payload: { show: false, message: t("share_content_loading") },
        });
        setSelectedUsers([]);
        dispatch({
          type: "alert/show",
          payload: {
            type: "error",
            title: t("share"),
            message: t("shared_content_error_alert_message"),
          },
        });
      });
  };

  const stringLength = isMobile ? 140 : 180;
  const courseDesc = `${t("share_this_description")} ${t(contentType?.toLowerCase())} ${t(
    "share_this_description_rest",
  )}`;
  const focusClass = useFocusStyles();

  return (
    <Dialog
      id="sharedialog"
      onClose={handleClose}
      role="dialog"
      aria-labelledby="share_dialog_label"
      aria-modal="true"
      open={open}
      minWidth={isMobile ? 0 : 474}
      showCloseIcon
      title={t("share")}
    >
      <DialogContent style={{ display: "flex", flexDirection: "column", overflowY: "visible" }}>
        <Typography
          style={{
            fontSize: "16px",
            fontWeight: isMobile ? "bold" : undefined,
            color: theme.palette.grey["800"],
          }}
          component="h2"
          id="share_dialog_label"
        >
          {shareTitle}
        </Typography>
        <Typography
          style={{
            fontSize: isMobile ? "14px" : "12px",
            color: theme.palette.grey["800"],
            lineHeight: "18px",
            marginTop: "15px",
            marginBottom: "26px",
          }}
          component="p"
        >
          {courseDesc.length > stringLength ? (
            <Tooltip
              title=""
              filter={(ele) => ele.tagName === "SPAN"}
              parentTitle={true}
              tooltipStyle={{ maxWidth: "200px" }}
            >
              <p title="" style={{ display: "inline" }}>
                {courseDesc.slice(0, stringLength)}
                <span
                  title={courseDesc.slice(stringLength, courseDesc.length)}
                  style={{ display: "inline" }}
                >
                  {"..."}
                </span>
              </p>
            </Tooltip>
          ) : (
            <RenderHTML content={courseDesc} />
          )}
        </Typography>
        <div style={{ opacity: "0" }} role="status" aria-atomic="true" aria-alive="assertive">
          {statusMessage}
        </div>
        <UserSearch
          setaddedUsers={setaddedUsers}
          addedUsers={addedUsers}
          setSelectedUsers={setSelectedUsers}
          selectedUsers={selectedUsers}
          setStatusMessage={setStatusMessage}
          outputHandler={() => {
            return false;
          }}
          label={t("recipient")}
          placeHolder={t("search")}
          isRoleBased={false}
          aria-autocomplete={"list"}
        />
        <UserSelected
          selectedUsers={selectedUsers}
          handleRemoveEnrollment={handleRemoveEnrollment}
          style={{ overflowY: "visible" }}
        />
      </DialogContent>
      <DialogActions>
        <Action type="dialog-alt" onClick={handleClose}>
          {t("cancel_and_close")}
        </Action>
        <Button
          variant="outlined"
          onClick={handleShareContent}
          disabled={selectedUsers.length === 0}
          style={{
            color:
              portalSettingsManager.buttonColors?.themed?.active?.text ||
              theme.palette.primary.main,
            backgroundColor: portalSettingsManager.buttonColors?.themed?.active?.back,
            border: `1px solid ${
              portalSettingsManager.buttonColors?.themed?.active?.border ||
              theme.palette.primary.main
            }`,
          }}
          className={`${focusClass.primaryHover} ${focusClass.focusItem}`}
        >
          {t("share").toUpperCase()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
